import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { formatTimestampPrecise } from '@/lib/dates'
import { Search, X, FileText, ChevronDown } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useIsAdmin } from '@/lib/auth'
import { useTicketActivity } from '@/queries/tickets'
import type { ActionTrailEvent } from '@/types/ticket'

dayjs.extend(relativeTime)

// Compact, deterministic colour by event-type family so the eye can scan the
// timeline. New event types fall through to the default outline variant.
const EVENT_TONE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  page_view: 'outline',
  session_start: 'secondary',
  session_end: 'secondary',
  automation_run_failed: 'destructive',
  import_rolled_back: 'destructive',
  invoice_force_completed: 'destructive',
}

const WINDOWS: Array<{ label: string; minutes: number }> = [
  { label: 'Snapshot', minutes: 0 },
  { label: '1 hour', minutes: 60 },
  { label: '4 hours', minutes: 240 },
  { label: '24 hours', minutes: 24 * 60 },
]

interface Props {
  ticketId: string
  ticketCreatedAt: string
  snapshot: ActionTrailEvent[]
  /**
   * Set when the trail is nested inside the rail metacard's own "Action trail"
   * disclosure, which already supplies the label and the padding — repeating
   * either here is what made the nested version read as two headings.
   */
  embedded?: boolean
}

export function ActionTrail({ ticketId, ticketCreatedAt, snapshot, embedded = false }: Props) {
  const isAdmin = useIsAdmin()
  const [windowMinutes, setWindowMinutes] = useState(0)
  const [direction, setDirection] = useState<'before' | 'both'>('before')
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState<ActionTrailEvent | null>(null)

  const live = useTicketActivity(
    ticketId,
    { minutes: windowMinutes, direction },
    isAdmin && windowMinutes > 0,
  )

  const usingLive = windowMinutes > 0 && Boolean(live.data)
  const events = usingLive ? (live.data?.data ?? []) : snapshot

  const eventTypes = useMemo(() => {
    const counts = new Map<string, number>()
    events.forEach(e => counts.set(e.event_type, (counts.get(e.event_type) ?? 0) + 1))
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [events])

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return events.filter(e => {
      if (selectedType && e.event_type !== selectedType) return false
      if (!needle) return true
      return (
        e.event_type.toLowerCase().includes(needle) ||
        (e.page_path ?? '').toLowerCase().includes(needle) ||
        (e.page_name ?? '').toLowerCase().includes(needle)
      )
    })
  }, [events, selectedType, search])

  // The trail is the reporter's product activity — staff-only. The API also
  // strips it server-side for non-admins; this just avoids an empty panel.
  // (Placed after all hooks to satisfy rules-of-hooks.)
  if (!isAdmin) return null

  return (
    <section className={embedded ? 'space-y-3' : 'px-5 py-4 space-y-3'}>
      <div className={embedded ? 'flex items-center justify-end' : 'flex items-center justify-between'}>
        {!embedded && (
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            Action trail
          </h3>
        )}
        {isAdmin && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1">
                {windowMinutes === 0
                  ? 'Snapshot'
                  : `${formatWindow(windowMinutes)} ${direction === 'both' ? 'around' : 'before'}`}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-xs">Window</DropdownMenuLabel>
              {WINDOWS.map(w => (
                <DropdownMenuItem
                  key={w.minutes}
                  onClick={() => setWindowMinutes(w.minutes)}
                  className={w.minutes === windowMinutes ? 'bg-accent' : ''}
                >
                  {w.label}
                </DropdownMenuItem>
              ))}
              {windowMinutes > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs">Direction</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => setDirection('before')}
                    className={direction === 'before' ? 'bg-accent' : ''}
                  >
                    Before ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDirection('both')}
                    className={direction === 'both' ? 'bg-accent' : ''}
                  >
                    Both sides
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isAdmin && windowMinutes > 0 && live.isLoading && (
        <p className="text-xs text-muted-foreground italic">Loading activity…</p>
      )}

      {eventTypes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {eventTypes.map(([type, count]) => {
            const active = selectedType === type
            return (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedType(active ? null : type)}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                  active
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background hover:bg-accent'
                }`}
              >
                <span>{type}</span>
                <span className={active ? 'opacity-80' : 'text-muted-foreground'}>{count}</span>
              </button>
            )
          })}
          {selectedType && (
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-accent"
            >
              <X className="h-3 w-3" /> clear
            </button>
          )}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter by page or event…"
          className="h-8 pl-8 text-xs"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No events match.</p>
      ) : (
        <ol className="space-y-1.5">
          {filtered.map((e, i) => {
            const beforeAnchor = dayjs(e.ts).isBefore(ticketCreatedAt)
            return (
              <li key={`${e.ts}-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(e)}
                  className="group w-full rounded border border-border/60 bg-background px-2.5 py-1.5 text-left hover:border-border hover:bg-accent/40 transition-colors"
                >
                  <div className="flex items-baseline gap-2 min-w-0">
                    <Badge
                      variant={EVENT_TONE[e.event_type] ?? 'outline'}
                      className="font-mono text-[10px] px-1.5 py-0 shrink-0"
                    >
                      {e.event_type}
                    </Badge>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {dayjs(e.ts).fromNow()}
                    </span>
                    {!beforeAnchor && (
                      <span className="text-[10px] text-amber-600 shrink-0">after</span>
                    )}
                    {e.page_path && (
                      <span className="text-xs text-foreground truncate min-w-0 font-mono">
                        {e.page_path}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            )
          })}
        </ol>
      )}

      <EventDetailDialog event={open} onClose={() => setOpen(null)} />
    </section>
  )
}

function formatWindow(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const hours = minutes / 60
  return hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`
}

function EventDetailDialog({ event, onClose }: { event: ActionTrailEvent | null; onClose: () => void }) {
  return (
    <Dialog open={Boolean(event)} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {event?.event_type ?? 'Event'}
          </DialogTitle>
        </DialogHeader>
        {event && (
          <div className="space-y-3 text-sm">
            <KV label="Timestamp" value={formatTimestampPrecise(event.ts)} />
            <KV label="Event" value={event.event_type} mono />
            {event.page_path && <KV label="Page" value={event.page_path} mono />}
            {event.page_name && <KV label="Page name" value={event.page_name} />}
            {event.session_id && <KV label="Session" value={event.session_id} mono small />}
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground mb-1">
                Properties
              </div>
              {Object.keys(event.properties ?? {}).length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No properties.</p>
              ) : (
                <pre className="max-h-72 overflow-auto rounded border bg-muted/40 p-2 text-[11px] font-mono leading-relaxed">
                  {JSON.stringify(event.properties, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function KV({ label, value, mono = false, small = false }: { label: string; value: string; mono?: boolean; small?: boolean }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2 items-baseline">
      <span className="text-[11px] uppercase tracking-[0.06em] text-muted-foreground">{label}</span>
      <span
        className={`break-all ${mono ? 'font-mono' : ''} ${small ? 'text-xs' : 'text-sm'}`}
      >
        {value}
      </span>
    </div>
  )
}
